import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import Pagination from 'react-bootstrap/Pagination'
import {
  usePagination,
  RIGHTDOTS,
  LEFTDOTS,
} from '../../../customHooks/AppTable/usePaginationHooks'
import {
  DEFAULT_PAGESIZE,
  PAGESIZE,
} from '../../../utils/constants/UIConstants'
import { AppPaginationProps } from './ATTableProps'
import ATDropdown from '../formComponent/ATDropdown'

const AppPagination: React.FC<AppPaginationProps> = ({
  onPageChange,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
  handlePageSize,
}) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  })

  const onNext = useCallback(() => {
    const nextPage = currentPage + 1
    onPageChange(nextPage)
  }, [currentPage, onPageChange])

  const getNoOfRecords = (
    totalCount: number,
    pageSize: number,
    currentPage: number,
  ) => {
    return currentPage * pageSize > totalCount
      ? totalCount
      : currentPage * pageSize
  }

  const onPrevious = useCallback(() => {
    const prevPage = currentPage - 1
    onPageChange(prevPage)
  }, [currentPage, onPageChange])

  const onChangeHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const pageNumber = Number((e.target as HTMLElement).dataset['key'])
      onPageChange(pageNumber)
    },
    [onPageChange],
  )

  const lastPage = paginationRange
    ? paginationRange[paginationRange?.length - 1]
    : 1
  if (currentPage === 0 || !paginationRange || paginationRange.length < 2) {
    return (
      <div className="d-flex justify-content-end appPagination">
        <span className="d-flex align-items-center">
          Viewing {currentPage * pageSize - pageSize + 1} - {totalCount} of{' '}
          {totalCount}
        </span>
        <span className="d-flex align-items-center">
          <ATDropdown
            name="page"
            options={PAGESIZE}
            onChange={handlePageSize}
            value={pageSize}
            isError={false}
            onBlurHandler={() => void 0}
            labelKey="value"
            valueKey="key"
            isSearchable={false}
            isClearable={false}
            defaultValue={DEFAULT_PAGESIZE}
            title=""
            error=""
          />
        </span>
      </div>
    )
  }
  return (
    <div className="d-flex justify-content-end appPagination">
      <span className="d-flex align-items-center">
        Viewing {currentPage * pageSize - pageSize + 1} -{' '}
        {getNoOfRecords(totalCount, pageSize, currentPage)} of {totalCount}
      </span>
      <Pagination className="m-0">
        <Pagination.First disabled={currentPage === 1} onClick={onPrevious} />
        {paginationRange?.map((pageNumber) => {
          if (pageNumber === RIGHTDOTS || pageNumber === LEFTDOTS) {
            return <Pagination.Ellipsis key={pageNumber} />
          }
          return (
            <Pagination.Item
              key={pageNumber}
              data-key={pageNumber}
              active={pageNumber === currentPage}
              onClick={onChangeHandler}
            >
              {pageNumber}
            </Pagination.Item>
          )
        })}
        <Pagination.Last onClick={onNext} disabled={currentPage === lastPage} />
      </Pagination>
      <span className="d-flex align-items-center">
        <ATDropdown
          name="page"
          options={PAGESIZE}
          onChange={handlePageSize}
          value={pageSize}
          isError={false}
          onBlurHandler={() => void 0}
          labelKey="value"
          valueKey="key"
          isSearchable={false}
          isClearable={false}
          defaultValue={DEFAULT_PAGESIZE}
          title=""
          error=""
        />
      </span>
    </div>
  )
}
AppPagination.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
  siblingCount: PropTypes.number,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  handlePageSize: PropTypes.func.isRequired,
}

export default AppPagination
